<template>
    <div>
        <!-- Header -->
        <template v-if="sesion">
            <div class="tw:border-b tw:border-slate-200 tw:px-6 tw:py-5">
                <div class="tw:flex tw:flex-col tw:gap-2 tw:items-start tw:md:flex-row tw:md:items-center tw:justify-between">
                    <div>
                        <h3 class="tw:mb-0 tw:text-xl tw:font-semibold tw:text-slate-900">
                            <i class="fas fa-ticket-alt tw:mr-2" aria-hidden="true"></i>
                            Reservas
                        </h3>
                        <p class="tw:mb-0 tw:text-sm tw:text-gray-600">
                            Gestiona la capacidad y las entradas reservadas para esta sesión.
                        </p>
                    </div>
                    <div>
                        <Button @click.prevent="abrirModalCrear">Crear Nueva Reserva</Button>
                    </div>
                </div>
            </div>

            <div class="tw:px-6 tw:py-6">
                <!-- Estado vacío -->
                <div v-if="!loading && reservas.length === 0" class="tw:text-center tw:py-10 tw:text-gray-500">
                    <i class="fas fa-ticket-alt tw:text-4xl tw:mb-3 tw:block tw:opacity-40"></i>
                    <p class="tw:text-lg">No hay reservas creadas para esta sesión.</p>
                </div>

                <!-- Loader -->
                <div v-else-if="loading" class="tw:flex tw:justify-center tw:py-10">
                    <LoaderComponent />
                </div>

                <!-- Tabla de reservas -->
                <div v-else :class="['table-wrapper', { 'table-wrapper--expanded': reservas.length <= 3 }]">
                    <table class="tw:min-w-full tw:divide-y tw:divide-slate-200 tw:text-sm">
                        <thead>
                            <tr class="tw:bg-slate-50">
                                <th class="tw:px-4 tw:py-3 tw:text-center tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wide tw:text-slate-500">Email</th>
                                <th class="tw:px-4 tw:py-3 tw:text-center tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wide tw:text-slate-500">Localizador</th>
                                <th class="tw:px-4 tw:py-3 tw:text-center tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wide tw:text-slate-500">Compradas / Total</th>
                                <th class="tw:px-4 tw:py-3 tw:text-center tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wide tw:text-slate-500">Estado</th>
                                <th class="tw:px-4 tw:py-3 tw:text-center tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wide tw:text-slate-500">Fecha</th>
                                <th class="tw:px-4 tw:py-3 tw:text-center tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wide tw:text-slate-500">Opciones</th>
                            </tr>
                        </thead>
                        <tbody class="tw:divide-y tw:divide-slate-100 tw:bg-white">
                            <tr v-for="(reserva, index) in reservas" :key="reserva.id" class="hover:tw:bg-slate-50">
                                <td class="tw:px-4 tw:py-3 tw:text-center">{{ reserva.email }}</td>
                                <td class="tw:px-4 tw:py-3 tw:text-center">{{ reserva.localizador || '—' }}</td>
                                <td class="tw:px-4 tw:py-3 tw:text-center">
                                    <span class="tw:font-medium">{{ reserva.units_claimed != null ? reserva.units_claimed : '—' }}</span>
                                    <span class="tw:text-gray-400"> / </span>
                                    <span>{{ reserva.units_total != null ? reserva.units_total : '—' }}</span>
                                </td>
                                <td class="tw:px-4 tw:py-3 tw:text-center">
                                    <span :class="getReservaStatusMeta(reserva).className">
                                        {{ getReservaStatusMeta(reserva).label }}
                                    </span>
                                </td>
                                <td class="tw:px-4 tw:py-3 tw:text-center">{{ reserva.created_at }}</td>
                                <td class="tw:px-4 tw:py-3 tw:text-center">
                                    <OpcionesDropdownTableComponent
                                        :lastIndex="reservas.length > 3 && index >= reservas.length - 3"
                                        :disabled="!hasReservaActions(reserva)"
                                        :options="getReservaDropdownOptions(reserva)"
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <TablePagination
                    v-if="totalReservas > perPage"
                    :value="currentPage"
                    :total="totalReservas"
                    :per-page="perPage"
                    @input="changePage"
                />
            </div>
        </template>

        <!-- Loader sesión -->
        <template v-else-if="loadingSesion">
            <div class="tw:flex tw:min-h-[220px] tw:items-center tw:justify-center tw:rounded-xl tw:border tw:border-slate-200 tw:bg-white tw:px-6 tw:py-6 tw:shadow-sm">
                <LoaderComponent />
            </div>
        </template>

        <!-- Modal crear reserva -->
        <ModalComponent
            name="crearReservaGenerica"
            title="Crear Nueva Reserva"
            v-on:close="cerrarModalCrear"
        >
            <ValidationObserver tag="form" ref="observer" @submit.prevent="submitCrear">
                <div class="tw:space-y-4">

                    <!-- Email -->
                    <ValidationProvider rules="required|email" v-slot="{ errors }" name="Email">
                        <div class="tw:space-y-2">
                            <label class="tw:font-medium tw:text-sm tw:text-gray-700">
                                Email <span class="tw:text-red-500">*</span>
                            </label>
                            <input
                                v-model="form.email"
                                type="email"
                                class="tw:w-full tw:rounded-md tw:border tw:bg-white tw:px-3 tw:py-2 tw:text-sm tw:text-slate-700 focus:tw:outline-none focus:tw:border-blue-400"
                                :class="errors.length ? 'tw:border-red-400 tw:bg-red-50' : 'tw:border-slate-300'"
                                placeholder="cliente@ejemplo.com"
                            />
                            <span v-if="errors.length" class="tw:block tw:text-xs tw:font-medium tw:text-red-600">{{ errors[0] }}</span>
                        </div>
                    </ValidationProvider>

                    <!-- Localizador (opcional) -->
                    <div class="tw:space-y-2">
                        <label class="tw:font-medium tw:text-sm tw:text-gray-700">
                            Localizador
                            <span class="tw:text-xs tw:text-gray-400 tw:font-normal">(opcional — se genera automáticamente si se deja vacío)</span>
                        </label>
                        <input
                            v-model="form.localizador"
                            type="text"
                            class="tw:w-full tw:rounded-md tw:border tw:border-slate-300 tw:bg-white tw:px-3 tw:py-2 tw:text-sm tw:text-slate-700 focus:tw:border-blue-400 focus:tw:outline-none"
                            placeholder="Ej: ABC-001"
                        />
                    </div>

                    <!-- Descuento (opcional) -->
                    <ValidationProvider rules="min_value:0|max_value:100" v-slot="{ errors }" name="Descuento">
                        <div class="tw:space-y-2">
                            <label class="tw:font-medium tw:text-sm tw:text-gray-700">
                                Descuento (%)
                                <span class="tw:text-xs tw:text-gray-400 tw:font-normal">(opcional — por defecto 0%)</span>
                            </label>
                            <input
                                v-model.number="form.descuento"
                                type="number"
                                min="0"
                                max="100"
                                class="tw:w-full tw:rounded-md tw:border tw:bg-white tw:px-3 tw:py-2 tw:text-sm tw:text-slate-700 focus:tw:outline-none focus:tw:border-blue-400"
                                :class="errors.length ? 'tw:border-red-400 tw:bg-red-50' : 'tw:border-slate-300'"
                                placeholder="0"
                            />
                            <span v-if="errors.length" class="tw:block tw:text-xs tw:font-medium tw:text-red-600">{{ errors[0] }}</span>
                        </div>
                    </ValidationProvider>

                    <!-- Comprar Completa -->
                    <div class="tw:my-2 tw:rounded-lg tw:border tw:border-slate-200 tw:bg-slate-50 tw:px-4 tw:py-3">
                        <div class="custom-control custom-checkbox">
                            <input
                                id="comprar-completa-check"
                                v-model="form.comprar_completa"
                                type="checkbox"
                                class="custom-control-input"
                            />
                            <label for="comprar-completa-check" class="custom-control-label tw:font-medium tw:text-sm tw:text-gray-700">
                                Comprar completa
                                <span class="tw:block tw:text-xs tw:text-gray-400 tw:font-normal">
                                    El cliente debe canjear todas las entradas de una vez
                                </span>
                            </label>
                        </div>
                    </div>

                    <!-- Filas de Precio + Unidades -->
                    <div class="tw:bg-gray-50 tw:p-4 tw:rounded-xl tw:border tw:border-gray-200 tw:mt-6">
                        <label class="tw:block tw:font-semibold tw:text-xs tw:uppercase tw:tracking-wider tw:text-gray-500 tw:mb-3">
                            Configuración de Entradas <span class="tw:text-red-500">*</span>
                        </label>

                        <div class="tw:space-y-3">
                            <div
                                v-for="(fila, index) in form.precios"
                                :key="index"
                                class="tw:flex tw:gap-3 tw:items-start tw:bg-white tw:p-3 tw:rounded-lg tw:border tw:border-gray-200 tw:shadow-sm tw:relative tw:group"
                            >
                                <!-- Selector de precio -->
                                <ValidationProvider
                                    :rules="'required'"
                                    v-slot="{ errors }"
                                    :name="'Precio ' + (index + 1)"
                                    class="tw:flex-1"
                                    tag="div"
                                >
                                    <label class="tw:block tw:text-[10px] tw:font-bold tw:text-gray-400 tw:uppercase tw:mb-1 tw:ml-1">Tipo de Entrada</label>
                                    <select
                                        v-model="fila.precio_id"
                                        class="tw:m-0 tw:h-[42px] tw:w-full tw:rounded-md tw:border tw:bg-white tw:px-3 tw:text-sm tw:text-slate-700 focus:tw:border-blue-400 focus:tw:outline-none"
                                        :class="errors.length ? 'tw:border-red-400 tw:bg-red-50' : 'tw:border-gray-200'"
                                        @change="handlePrecioChange(index)"
                                    >
                                        <option value="" disabled>— Seleccionar —</option>
                                        <option
                                            v-for="entrada in entradas"
                                            :key="entrada.id"
                                            :value="entrada.id"
                                            :disabled="isEntradaDisabledForRow(entrada.id, index)"
                                        >
                                            {{ entrada.descripcion }} ({{ entrada.precio | formatear_precio }})
                                        </option>
                                    </select>
                                    <span v-if="errors.length" class="tw:block tw:m-0 tw:mt-1 tw:text-[11px] tw:font-medium tw:text-red-600">{{ errors[0] }}</span>
                                </ValidationProvider>

                                <!-- Input de unidades -->
                                <ValidationProvider
                                    :rules="'required|min_value:1' + (stockParaFila(index) !== null ? '|max_value:' + stockParaFila(index) : '')"
                                    v-slot="{ errors }"
                                    :name="'Unidades ' + (index + 1)"
                                    class="tw:w-32"
                                    tag="div"
                                >
                                    <label class="tw:block tw:text-[10px] tw:font-bold tw:text-gray-400 tw:uppercase tw:mb-1 tw:ml-1">Cantidad</label>
                                    <div class="tw:relative">
                                        <input
                                            v-if="stockCompartidoSesion === 0 || (stockCompartidoSesion !== null && stockParaFila(index) === 0)"
                                            type="number"
                                            class="tw:m-0 tw:h-[42px] tw:w-full tw:rounded-md tw:border tw:bg-gray-100 tw:text-center tw:font-bold tw:text-gray-400 tw:cursor-not-allowed"
                                            disabled
                                            placeholder="0"
                                            :aria-describedby="`reservas-sin-cupo-${index}`"
                                        />
                                        <input
                                            v-else
                                            v-model.number="fila.units_total"
                                            type="number"
                                            min="1"
                                            :max="stockParaFila(index) !== null ? stockParaFila(index) : undefined"
                                            @input="onCantidadInput(index, $event)"
                                            class="tw:m-0 tw:h-[42px] tw:w-full tw:rounded-md tw:border tw:bg-white tw:text-center tw:font-bold tw:text-gray-700 focus:tw:border-blue-400 focus:tw:outline-none"
                                            :class="errors.length ? 'tw:border-red-400 tw:bg-red-50' : 'tw:border-gray-200'"
                                            placeholder="1"
                                            :aria-describedby="stockCompartidoSesion !== null ? `reservas-cupo-max-${index}` : undefined"
                                        />
                                    </div>
                                    <span
                                        v-if="errors.length && stockCompartidoSesion !== 0"
                                        :id="`reservas-cupo-error-${index}`"
                                        class="tw:block tw:m-0 tw:mt-1 tw:text-[11px] tw:font-medium tw:text-red-600"
                                    >{{ errors[0] }}</span>
                                    <span
                                        v-if="stockCompartidoSesion === 0"
                                        :id="`reservas-sin-cupo-${index}`"
                                        role="status"
                                        class="tw:block tw:text-[11px] tw:text-gray-500 tw:mt-0.5 tw:ml-1"
                                    >Sin cupo disponible</span>
                                    <span
                                        v-else-if="stockCompartidoSesion !== null && stockParaFila(index) === 0"
                                        :id="`reservas-sin-cupo-fila-${index}`"
                                        role="status"
                                        class="tw:block tw:text-[11px] tw:text-gray-500 tw:mt-0.5 tw:ml-1"
                                    >Sin cupo disponible</span>
                                    <span
                                        v-else-if="stockCompartidoSesion !== null && stockParaFila(index) !== null"
                                        :id="`reservas-cupo-max-${index}`"
                                        class="tw:block tw:text-[11px] tw:font-medium tw:text-gray-600 tw:mt-0.5 tw:ml-1"
                                    >Máximo permitido: {{ stockParaFila(index) }}</span>
                                    <span v-else-if="stockParaFila(index) !== null" class="tw:block tw:text-[10px] tw:text-gray-400 tw:mt-0.5 tw:ml-1">
                                        Máx: {{ stockParaFila(index) }}
                                    </span>
                                    <span v-else class="tw:block tw:text-[10px] tw:text-gray-400 tw:mt-0.5 tw:ml-1">
                                        El stock final se valida al crear la reserva.
                                    </span>
                                </ValidationProvider>

                                <!-- Botón quitar fila -->
                                <div class="tw:pt-5">
                                    <button
                                        v-if="form.precios.length > 1"
                                        type="button"
                                        class="tw:h-[42px] tw:w-[42px] tw:flex tw:items-center tw:justify-center tw:rounded-md tw:bg-red-50 tw:text-red-400 hover:tw:bg-red-500 hover:tw:text-white tw:transition-all tw:duration-200 tw:border tw:border-red-100"
                                        @click="quitarFila(index)"
                                        title="Eliminar fila"
                                    >
                                        <i class="fas fa-trash-alt tw:text-sm"></i>
                                    </button>
                                </div>
                            </div>
                        </div>

                        <!-- Botón añadir fila -->
                        <div class="tw:mt-4">
                            <button
                                type="button"
                                class="tw:px-4 tw:py-2 tw:text-xs tw:font-bold tw:text-blue-600 tw:bg-blue-50 tw:rounded-full tw:border tw:border-blue-100 hover:tw:bg-blue-600 hover:tw:text-white tw:transition-all tw:duration-200 tw:flex tw:items-center tw:gap-2 tw:shadow-sm"
                                @click="agregarFila"
                            >
                                <i class="fas fa-plus-circle"></i> AÑADIR OTRO PRECIO
                            </button>
                        </div>
                    </div>

                    <!-- Footer del modal -->
                    <div class="tw:flex tw:justify-end tw:pt-4 tw:border-t tw:border-gray-100">
                        <Button type="button" :disabled="loadingCrear" @click.prevent="submitCrear">
                            <span v-if="loadingCrear">Creando...</span>
                            <span v-else>Crear Reserva</span>
                        </Button>
                    </div>

                </div>

            </ValidationObserver>
        </ModalComponent>

        <!-- Modal borrar reserva -->
        <BorrarModalComponent
            name="borrarReservaGenerica"
            title="Borrar Reserva"
            borrarTitle="¿Estás seguro de que quieres borrar esta reserva?"
            confirmButton="SI, BORRAR"
            @confirm="handleConfirmarBorrar"
            @close="handleCerrarModalBorrar"
        />
    </div>
</template>

<script setup>
    import { ref } from 'vue';
    import { getCurrentInstance } from 'vue';
    import { useRouter } from 'vue-router/composables';
    import { useReservasGenericas } from '@/composables/admin/events/sesiones/useReservasGenericas';
    import ModalComponent from '@/modules/admin/ui/ModalComponent.vue';
    import BorrarModalComponent from '@/modules/admin/ui/BorrarModalComponent.vue';
    import OpcionesDropdownTableComponent from '@/modules/admin/ui/OpcionesDropdownTableComponent.vue';
    import TablePagination from '@/modules/admin/ui/table/TablePagination.vue';

    const props = defineProps({
        evento_id: { type: [Number, String], required: true },
        sesion_id: { type: [Number, String], required: true },
    });

    const STATUS_BADGE_BASE_CLASS = 'tw:inline-flex tw:items-center tw:px-2 tw:py-0.5 tw:rounded tw:text-xs tw:font-medium';
    const RESERVA_STATUS_META = {
        pending: {
            label: 'Pendiente',
            className: 'tw:bg-yellow-100 tw:text-yellow-800',
        },
        completed: {
            label: 'Completada',
            className: 'tw:bg-green-100 tw:text-green-800',
        },
        cancelled: {
            label: 'Cancelada',
            className: 'tw:bg-red-100 tw:text-red-800',
        },
        processing: {
            label: 'En Proceso',
            className: 'tw:bg-blue-100 tw:text-blue-800',
        },
        unknown: {
            label: 'Desconocido',
            className: 'tw:bg-gray-100 tw:text-gray-800',
        },
    };
    const RESERVA_ACTION_LABELS = {
        'copy-link': 'Copiar enlace',
        'resend-email': 'Reenviar email',
        'delete': 'Borrar',
        'view-order': 'Ver pedido',
    };

    // ── INSTANCIAS DE VUE 2.7 ─────────────────────────────────────────────────────
    // En Vue 2.7 <script setup>, getCurrentInstance() provee acceso a this.$modal, $toast, $store, config
    const instance = getCurrentInstance();
    const vm = instance.proxy;
    const router = useRouter();

    // ── REF PARA OBSERVER DE VEEVALIDATE ─────────────────────────────────────────
    const observer = ref(null);

    // ── COMPOSABLE DE DOMINIO ─────────────────────────────────────────────────────
    const {
        sesion,
        entradas,
        reservas,
        loadingSesion,
        loading,
        loadingCrear,
        form,
        stockCompartidoSesion,
        stockParaFila,
        normalizePrecioId,
        getReservaStatusKey,
        getReservaActionIntents,
        buildReservaLink,
        agregarFila,
        quitarFila,
        onPrecioChange,
        resetForm,
        crearReserva,
        seleccionarParaBorrar,
        limpiarSeleccionBorrar,
        confirmarBorrar,
        reenviarEmail,
        verPedido,
        currentPage,
        perPage,
        totalReservas,
        changePage,
    } = useReservasGenericas({
        eventoId: props.evento_id,
        sesionId: props.sesion_id,
        config: vm.config,
        toast: vm.$toast,
        router,
        get urlGracias() {
            return vm.$store.getters.getFooter?.url_gracias ?? '';
        },
    });

    // REQ-SC-RES-06: clamp duro al CAP POR FILA (cup - suma_otras_filas)
    // cuando la sesión tiene `stock_compartido === true`. Lee `stockParaFila`
    // del composable, que ya descuenta las otras filas del cup compartido y
    // cae al legacy per-entrada cuando `stock_compartido === false`.
    // Reglas de no-op (dejamos que la entrada sea lo que el usuario tecleó):
    //   - `stockParaFila(index) === null`  → fila sin precio_id o precio_id
    //     desconocido, no podemos clavar cap (legacy VeeValidate).
    //   - `stockCompartidoSesion === 0`    → cup agotado a nivel sesión, el
    //     input ya está disabled y la rama "Sin cupo" se renderiza.
    function onCantidadInput(index, event) {
        const fila = form.value?.precios?.[index];
        if (!fila || !event || !event.target) {
            return;
        }
        const cap = stockParaFila.value(index);
        if (cap === null || cap <= 0) {
            return;
        }
        let val = Number(event.target.value);
        if (!Number.isFinite(val)) {
            event.target.value = '';
            return;
        }
        if (val > cap) {
            val = cap;
        }
        if (val < 1) {
            val = 1;
        }
        event.target.value = String(val);
        fila.units_total = val;
    }

    // ── HANDLERS DE MODAL ─────────────────────────────────────────────────────────

    function abrirModalCrear() {
        resetForm(observer.value);
        vm.$modal.show('crearReservaGenerica');
    }

    function cerrarModalCrear() {
        vm.$modal.hide('crearReservaGenerica');
        resetForm(observer.value);
    }

    function getReservaStatusMeta(reserva) {
        const statusKey = getReservaStatusKey(reserva.status);
        const meta = RESERVA_STATUS_META[statusKey] || RESERVA_STATUS_META.unknown;

        return {
            label: meta.label,
            className: `${STATUS_BADGE_BASE_CLASS} ${meta.className}`,
        };
    }

    async function copiarEnlace(reserva) {
        const clipboard = typeof navigator !== 'undefined' ? navigator.clipboard : null;

        if (!clipboard?.writeText) {
            vm.$toast.error('El navegador no permite copiar al portapapeles');
            return;
        }

        try {
            await clipboard.writeText(buildReservaLink(reserva));
            vm.$toast.success('Enlace copiado al portapapeles');
        } catch (_error) {
            vm.$toast.error('No se pudo copiar el enlace');
        }
    }

    function getReservaDropdownOptions(reserva) {
        const actionHandlers = {
            'copy-link': copiarEnlace,
            'resend-email': reenviarEmail,
            'delete': abrirModalBorrar,
            'view-order': verPedido,
        };

        return getReservaActionIntents(reserva, { onDelete: abrirModalBorrar }).map(intent => ({
            type: 'action',
            label: RESERVA_ACTION_LABELS[intent],
            action: () => actionHandlers[intent](reserva),
        }));
    }

    function hasReservaActions(reserva) {
        return getReservaDropdownOptions(reserva).length > 0;
    }

    function abrirModalBorrar(reserva) {
        seleccionarParaBorrar(reserva);
        vm.$modal.show('borrarReservaGenerica');
    }

    function handleCerrarModalBorrar() {
        vm.$modal.hide('borrarReservaGenerica');
        limpiarSeleccionBorrar();
    }

    // ── HANDLER PRECIO CHANGE (necesita $set de Vue 2) ────────────────────────────

    function handlePrecioChange(index) {
        const resultado = onPrecioChange(index);
        if (resultado) {
            vm.$set(form.value.precios, resultado.index, resultado.fila);
        }
    }

    function isEntradaDisabledForRow(entradaId, index) {
        const normalizedEntradaId = normalizePrecioId(entradaId);

        return form.value.precios.some((precio, precioIndex) => {
            return precioIndex !== index && normalizePrecioId(precio.precio_id) === normalizedEntradaId;
        });
    }

    // ── SUBMIT CREAR ──────────────────────────────────────────────────────────────

    async function submitCrear() {
        const isValid = await observer.value.validate();
        if (!isValid) return;

        crearReserva(() => {
            cerrarModalCrear();
        });
    }

    // ── CONFIRMAR BORRAR ──────────────────────────────────────────────────────────

    function handleConfirmarBorrar() {
        confirmarBorrar(() => {
            handleCerrarModalBorrar();
        });
    }
</script>

<style scoped>
.table-wrapper {
    overflow: auto;
    border: 1px solid #e2e8f0;
    border-radius: 0.75rem;
}

.table-wrapper--expanded {
    padding-bottom: 150px !important;
}
</style>
